import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { useAccountCompanyId } from 'application/hooks';
import { ModalTypes } from 'application/providers/ModalProvider';
import {
  selectIsAcceptanceAdvancedPolling,
  selectIsAcceptanceAdvancedSigningLoading,
  startAcceptanceAdvancedSigning,
  openSigningPreloadTab,
  selectAcceptanceAdvancedContext,
} from 'application/store';
import { INTEGRATION_PAGE_PATH } from 'application/consts';
import { RouterUtils } from 'application/providers/RouterProvider';
import { useAppDispatch, useAppSelector } from 'application/providers/StoreProvider';
import { DEFAULT_ACCEPTANCE_ADVANCED_ID } from 'application/store/slices/acceptanceAdvancedSlice/consts';

import {
  AcceptanceAdvanceStatus,
  AcceptanceCancellationReason,
  AuthCurrentUserDocument,
  AuthCurrentUserQuery,
  BillingCompanyBillingInfoDocument,
  RequisiteRuPureType,
} from '@shared/api/graphql/generated';
import { LicenseType, useAcceptanceAdvanced, useMarkAcceptanceErrorAsViewed } from '@entities/billing';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

import { useApolloClient } from 'application/providers/DataProvider';

import { UseSberBusinessOnlineViewModel } from './types';

const SBBOL_MESSAGE = 'SBBOL_BINDING_SUCCESS';
const SBBOL_REDIRECT = 'SBBOL_REDIRECT';
const IS_NEED_SHOW_UNPAID_LICENSES_MODAL = 'IS_NEED_SHOW_UNPAID_LICENSES_MODAL';
const URI_FOR_RETURN = `${window.location.origin}/${RouterUtils.generatePath(INTEGRATION_PAGE_PATH, { type: 'sbbol' })}`;

interface Props {
  isModalOpen: boolean;
  licensesCount: number;
  hasTrialLicenses: boolean;
  hasInactiveLicenses: boolean;
  hasActiveLicenses: boolean;
  renewableLicenses: LicenseType[];
  nonRenewableLicenses: LicenseType[];
  unpaidLicenses: LicenseType[];
  isDesktop: boolean;
  isIos: boolean;
  showCreatedInfoNotification: () => void;
  showPendingInfoNotification: () => void;
  showCredsMismatchCancelledNotification: () => void;
  showActiveTrialNotification: () => void;
  showActiveNotification: (isViewMode?: boolean) => void;
  closeAllModals: () => void;
  hideInfoNotification: () => void;
  openModal: (type: ModalTypes, props: {}, onCloseCallback?: () => void) => void;
}

const useSberBusinessOnlineViewModel = ({
  isModalOpen,
  licensesCount,
  hasTrialLicenses,
  hasInactiveLicenses,
  hasActiveLicenses,
  nonRenewableLicenses,
  unpaidLicenses,
  isDesktop,
  isIos,
  showCreatedInfoNotification,
  showPendingInfoNotification,
  showCredsMismatchCancelledNotification,
  showActiveTrialNotification,
  showActiveNotification,
  openModal,
  closeAllModals,
  hideInfoNotification,
}: Props): UseSberBusinessOnlineViewModel => {
  const isPolling = useAppSelector(selectIsAcceptanceAdvancedPolling());
  const startSigningAcceptanceAdvancedLoading = useAppSelector(selectIsAcceptanceAdvancedSigningLoading());
  const acceptanceAdvancedContext = useAppSelector(selectAcceptanceAdvancedContext(DEFAULT_ACCEPTANCE_ADVANCED_ID));

  const dispatch = useAppDispatch();

  const companyId = useAccountCompanyId();
  const apolloClient = useApolloClient();

  const { data: userData } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const currentCompany = userData?.currentUserPureQuery?.mainCompany;
  const currentRequisite = currentCompany?.requisite as RequisiteRuPureType;

  const [isReturnedFromIntegrationPage, setIsReturnedFromIntegrationPage] = useState<boolean>(false);

  const { acceptanceAdvanced, loading: acceptanceAdvancedLoading, refetch } = useAcceptanceAdvanced(companyId);
  const { request: markAcceptanceErrorAsViewed } = useMarkAcceptanceErrorAsViewed(refetch);

  const currentUserId = userData?.currentUserPureQuery?.id;

  const status = acceptanceAdvanced?.status || null;
  const payerAccount = acceptanceAdvanced?.payerAccount;
  const cancellationReason = acceptanceAdvanced?.cancellationReason;
  const payerInn = acceptanceAdvanced?.payerInn ?? '';
  const payerName = acceptanceAdvanced?.payerName ?? '';
  const acceptanceUserId = acceptanceAdvanced?.userId;
  const errorViewedAt = acceptanceAdvanced?.errorViewedAt;

  const onStartSigningAcceptanceAdvanced = async () => {
    localStorage.setItem(SBBOL_REDIRECT, 'true');

    const hasActiveTab = acceptanceAdvancedContext?.hasActiveTab;
    const preOpenedTab = hasActiveTab ? null : openSigningPreloadTab();

    dispatch(
      startAcceptanceAdvancedSigning({
        companyId,
        uriForReturn: URI_FOR_RETURN,
        fallbackFn: () => {
          openModal(ModalTypes.SBERBUSSINESS_OPEN, {});
        },
        preOpenedTab,
      })
    );

    if (!isIos) {
      showCreatedInfoNotification();
    }
  };

  const handleOpenLicensesModal = () => {
    if (!isModalOpen) {
      if (hasTrialLicenses && licensesCount !== 0) {
        openModal(ModalTypes.LICENSES, {
          openModal,
          isViewMode: unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0,
          unpaidLicensesOnly: false,
        });

        return;
      }

      if (hasInactiveLicenses && licensesCount !== 0) {
        openModal(ModalTypes.LICENSES, {
          openModal,
          isViewMode: false,
          unpaidLicensesOnly: true,
        });

        return;
      }

      if (hasActiveLicenses && licensesCount !== 0) {
        const unpaidLicensesOnly = localStorage.getItem(IS_NEED_SHOW_UNPAID_LICENSES_MODAL) === 'true';

        openModal(ModalTypes.LICENSES, {
          openModal,
          isViewMode: unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0,
          unpaidLicensesOnly,
        });
      }
    } else if ((unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0) || hasTrialLicenses) {
      closeAllModals();
      openModal(ModalTypes.LICENSES, {
        openModal,
        isViewMode: true,
        unpaidLicensesOnly: false,
      });
    }
  };

  const handleStopProcess = () => {
    hideInfoNotification();
    handleOpenLicensesModal();
    localStorage.removeItem(SBBOL_REDIRECT);
    localStorage.removeItem(IS_NEED_SHOW_UNPAID_LICENSES_MODAL);

    if (isDesktop) {
      if (hasTrialLicenses) {
        showActiveTrialNotification();
      } else {
        showActiveNotification();
      }
    }
  };

  useEffect(() => {
    switch (status) {
      case AcceptanceAdvanceStatus.Canceled: {
        if (cancellationReason === AcceptanceCancellationReason.CredentialsMismatch) {
          const acceptanceId = acceptanceAdvanced?.id ?? '';
          const shouldShow = acceptanceUserId === currentUserId && !errorViewedAt;

          if (shouldShow) {
            openModal(
              ModalTypes.ACCEPTANCE_CREDS_MISMATCH_ERROR,
              {
                payerInn,
                payerName,
                companyInn: currentRequisite?.inn,
                companyName: currentRequisite?.organizationFullname,
              },
              async () => {
                await markAcceptanceErrorAsViewed(companyId, acceptanceId);
                showCredsMismatchCancelledNotification();
              }
            );
          }
        }
        break;
      }
      default: {
        break;
      }
    }
  }, [status]);

  useEffect(() => {
    switch (status) {
      case AcceptanceAdvanceStatus.Pending: {
        if (!isIos) {
          showPendingInfoNotification();
        }

        break;
      }
      case AcceptanceAdvanceStatus.Authorized: {
        if (isPolling || localStorage.getItem(SBBOL_REDIRECT) === 'true') {
          handleStopProcess();
        }

        break;
      }
      case AcceptanceAdvanceStatus.Active: {
        if (isPolling || localStorage.getItem(SBBOL_REDIRECT) === 'true') {
          handleStopProcess();
        }

        break;
      }
      case null: {
        break;
      }
      default: {
        break;
      }
    }
  }, [status, isPolling]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data.type !== SBBOL_MESSAGE) {
        return;
      }

      apolloClient.refetchQueries({ include: [BillingCompanyBillingInfoDocument] });
      setIsReturnedFromIntegrationPage(true);
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [apolloClient]);

  useEffect(() => {
    if (isReturnedFromIntegrationPage) {
      if (status === AcceptanceAdvanceStatus.Pending) {
        showPendingInfoNotification();
      }
    }
  }, [isReturnedFromIntegrationPage]);

  const sberAuthButtonVisible = Boolean(currentCompany?.sberIdButtonState?.isVisible);
  const hasSberConnection = Boolean(userData?.currentUserPureQuery?.sberBusinessClientInfo?.hasSberBusinessAccount);

  return {
    acceptanceAdvancedStatus: status,
    payerAccount: payerAccount || '',
    startSigningAcceptanceAdvancedLoading,
    acceptanceAdvancedLoading,
    sberAuthButtonVisible,
    hasSberConnection,
    onStartSigningAcceptanceAdvanced,
  };
};

export default useSberBusinessOnlineViewModel;
